import { useEffect, useSyncExternalStore } from 'react';

/**
 * Mehrfachauswahl in der Liste (Paket P8). Ein kleiner Speicher außerhalb von React: jede Zeile fragt nur „bin ich
 * ausgewählt?“ ab und zeichnet nur neu, wenn sich genau das ändert (Leistung bei großen Listen, Messbericht P5).
 *
 * Wege: Strg/⌘+Klick schaltet um, Umschalt+Klick wählt einen Bereich, Umschalt+Pfeil erweitert per Tastatur, am
 * Handy „Auswählen“ im Zeilenmenü (danach schaltet jedes Antippen um). Esc hebt die Auswahl auf.
 */
class Selection {
  private ids = new Set<string>();
  private anchor: string | null = null;
  /** Auswahlmodus (Handy): Antippen schaltet um statt zu bearbeiten */
  mode = false;
  /** Reihenfolge der sichtbaren Zeilen (wie angezeigt, auch über Abschnitte hinweg) */
  order: () => string[] = () => [...document.querySelectorAll<HTMLElement>('main li[data-task-id]')].map((el) => el.dataset.taskId!);
  private listeners = new Set<() => void>();
  private version = 0;

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  };
  getVersion = () => this.version;
  has = (id: string) => this.ids.has(id);
  get size() {
    return this.ids.size;
  }
  all(): string[] {
    return [...this.ids];
  }

  toggle(id: string) {
    if (this.ids.has(id)) this.ids.delete(id);
    else this.ids.add(id);
    this.anchor = id;
    this.emit();
  }

  add(...ids: string[]) {
    for (const id of ids) this.ids.add(id);
    this.emit();
  }

  /** Bereich vom zuletzt angeklickten Eintrag bis hier (in der sichtbaren Reihenfolge) */
  range(id: string) {
    const order = this.order();
    const a = this.anchor ? order.indexOf(this.anchor) : -1;
    const b = order.indexOf(id);
    if (a < 0 || b < 0) return this.toggle(id);
    for (const x of order.slice(Math.min(a, b), Math.max(a, b) + 1)) this.ids.add(x);
    this.emit();
  }

  /** Nachprüfung 0.111 U02: Auswahlmodus ausdrücklich betreten („Aufgaben auswählen“) – noch ohne Auswahl */
  enter() {
    if (this.mode) return;
    this.mode = true;
    this.emit();
  }

  /** Alle angezeigten Zeilen (sichtbare Reihenfolge) – nie Verborgenes */
  selectShown() {
    this.mode = true;
    for (const id of this.order()) if (!id.startsWith('tmp-')) this.ids.add(id);
    this.emit();
  }

  start(id: string) {
    this.mode = true;
    this.ids.add(id);
    this.anchor = id;
    this.emit();
  }

  clear() {
    if (!this.ids.size && !this.mode) return;
    this.ids.clear();
    this.anchor = null;
    this.mode = false;
    this.emit();
  }

  /** Nicht mehr sichtbare Einträge (gelöscht, erledigt, verschoben) fallen aus der Auswahl */
  retain(visible: Set<string>) {
    let changed = false;
    for (const id of this.ids) if (!visible.has(id)) (this.ids.delete(id), (changed = true));
    if (changed) this.emit();
  }

  private emit() {
    this.version++;
    this.listeners.forEach((l) => l());
  }
}

export const selection = new Selection();

/** Ist diese Zeile ausgewählt? Zeichnet nur bei Änderung dieser einen Antwort neu */
export function useSelected(id: string): boolean {
  return useSyncExternalStore(selection.subscribe, () => selection.has(id));
}

/** U02: Auswahlmodus aktiv (auch ohne Auswahl) – Zeilen zeigen dann ihr Auswahlkästchen */
export function useSelectionMode(): boolean {
  return useSyncExternalStore(selection.subscribe, () => selection.mode);
}

/** Anzahl ausgewählter Einträge (für die Auswahlleiste) */
export function useSelectionSize(): number {
  return useSyncExternalStore(selection.subscribe, () => selection.size);
}

/**
 * Esc hebt die Auswahl auf – im Startpaket (die Auswahlleiste kommt getrennt geladen und wäre beim ersten Esc noch
 * nicht da). Erfassungsphase: Knöpfe und Zeilen halten Tasten sonst zurück. Offene Menüs/Fenster schließt Esc zuerst.
 */
export function useSelectionEscape(size: number, mode = false) {
  useEffect(() => {
    if (!size && !mode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('[role="dialog"], [role="menu"], [data-radix-popper-content-wrapper]')) selection.clear();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [size, mode]);
}
